/**
 * Analista del Portal Tickets (EF `analista-tickets`): contrato puro de la llamada.
 *
 * La función acepta `id_conversacion` (el `numero` de `ia_conversaciones`) y responde el suyo,
 * guardando cada turno en `ia_conversaciones` / `ia_mensajes` del agente `bot-agente-tickets`.
 * Mientras una versión anterior de la función no lo devuelva, el front sigue mandando el
 * `historial` del hilo como siempre: con número, la función ya tiene el contexto y no se manda.
 */
import { sanearHerramientas, type HerramientaIa } from "@/lib/chatIa";

export const ANALISTA_FUNCION = "analista-tickets";

export interface TurnoHistorialAnalista {
  rol: "usuario" | "analista";
  texto: string;
}

export interface CuerpoAnalista {
  pregunta: string;
  scope: string;
  desde: string;
  hasta: string;
  filtros: Record<string, unknown>;
  id_conversacion?: number;
  historial?: TurnoHistorialAnalista[];
}

/** Cuerpo de la llamada: con conversación guardada va su número; sin ella, el hilo completo. */
export function cuerpoAnalista(v: {
  pregunta: string;
  idConversacion: number | null;
  hilo: ReadonlyArray<{ rol: "usuario" | "asistente"; texto: string }>;
  filtros: { scope: string; desde: string; hasta: string };
}): CuerpoAnalista {
  const base = { pregunta: v.pregunta, scope: v.filtros.scope, desde: v.filtros.desde, hasta: v.filtros.hasta, filtros: {} };
  if (v.idConversacion !== null) return { ...base, id_conversacion: v.idConversacion };
  return {
    ...base,
    historial: v.hilo.map((m) => ({ rol: m.rol === "usuario" ? "usuario" : "analista", texto: m.texto })),
  };
}

const ERRORES: Record<string, string> = {
  sin_permiso: "No tienes permiso para usar este asistente.",
  deshabilitado: "El asistente está apagado por ahora.",
  error_permiso: "No pude verificar tu permiso. Intenta de nuevo en un momento.",
  limite_alcanzado: "Llegaste al límite de preguntas de esta hora.",
};

export type RespuestaAnalista =
  | {
      ok: true;
      texto: string;
      herramientas: HerramientaIa[];
      /** `null` = la función aún no guarda conversaciones. */
      idConversacion: number | null;
      restantesHora: number | null;
    }
  | { ok: false; codigo: string; mensaje: string };

const esObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

const numero = (v: unknown): number | null => {
  const n = typeof v === "string" && v.trim() !== "" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

/** Lee el 200 de la función. Fuera de contrato o `ok: false` = error legible, nunca un código crudo. */
export function leerRespuestaAnalista(data: unknown): RespuestaAnalista {
  if (!esObjeto(data)) return { ok: false, codigo: "error_modelo", mensaje: "No pude responder." };
  const texto = typeof data.respuesta === "string" ? data.respuesta.trim() : "";
  if (data.ok !== true || !texto) {
    const codigo = typeof data.error === "string" && data.error ? data.error : "error_modelo";
    const mensaje =
      ERRORES[codigo] ??
      (typeof data.mensaje === "string" && data.mensaje.trim() ? data.mensaje.trim() : null) ??
      (codigo !== "error_modelo" ? codigo : "No pude responder.");
    return { ok: false, codigo, mensaje };
  }
  const restantes = numero(data.restantes_hora);
  return {
    ok: true,
    texto,
    herramientas: sanearHerramientas(data.herramientas_usadas),
    idConversacion: numero(data.id_conversacion),
    restantesHora: restantes === null ? null : Math.max(0, Math.floor(restantes)),
  };
}

/** Lo que copia "Copiar plan": sin los `**` del formato. */
export const textoParaCopiarAnalista = (texto: string) => texto.replace(/\*\*/g, "");
